import Chart from "chart.js/auto";
import { API, BlockTool } from "@editorjs/editorjs";

/**
 * Type definition for an individual chart dataset, including label, data values, and background colors.
 */
type ChartDataset = {
  label: string;
  data: number[] | { x: number; y: number }[];
  backgroundColor: string[];
  fill?: boolean;
};

/**
 * Type definition for the overall chart data, including labels, datasets, chart type, and dimensions.
 */
type ChartData = {
  title: string;
  type: string;
  labels: string[];
  datasets: ChartDataset[];
  width: number;
  height: number;
};

// Default background colors for datasets
const backgroundColor = [
  "#d62828",
  "#ff5d00",
  "#7cb0e3",
  "#f57c00",
  "#1b82b2",
  "#ff3e8e",
  "#7239e3",
  "#306eff",
  "#05c58a",
  "#ffca22",
  "#d71f33",
  "#ff9a00",
];

/**
 * ChartBlock is an Editor.js custom block tool for creating and editing charts using Chart.js.
 */
class ChartBlock implements BlockTool {
  api: API;
  data: ChartData;
  chartInstance: Chart | null = null;
  container: HTMLElement | null = null;
  readOnly = false;

  static isReadOnlySupported = true;

  /**
   * Initializes the block with default or provided chart data.
   * @param data - Initial chart data.
   * @param api - Editor.js API for interacting with the editor.
   */
  constructor({
    data,
    api,
    readOnly,
  }: {
    data: ChartData | undefined;
    api: API;
    readOnly: boolean;
  }) {
    this.api = api;
    this.readOnly = readOnly;
    this.data = {
      title: data?.title ?? "New Chart",
      type: data?.type ?? "bar",
      labels: data?.labels ?? ["Label 1", "Label 2"],
      datasets: data?.datasets ?? [
        {
          label: "Dataset 1",
          data: [10, 20],
          backgroundColor: backgroundColor,
        },
      ],
      width: data?.width ?? 400,
      height: data?.height ?? 400,
    };
    this.readOnly = readOnly;
  }

  /**
   * Returns the toolbox configuration for the block, including title and icon.
   */
  static get toolbox() {
    return {
      title: "Chart",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 20V14H14V20H19V10H14V4H10V10H5V20H10Z" fill="currentColor"/></svg>',
    };
  }

  /**
   * Renders the chart block, including the chart canvas and edit functionality.
   * @returns The block's container element.
   */
  render(): HTMLElement {
    this.container = document.createElement("div");
    this.container.classList.add("chart-container", "mb-5", "mt-4");
    this.container.style.height = `${this.data.height}px`;
    this.container.style.width = `${this.data.width}px`;

    const canvas = document.createElement("canvas");
    canvas.width = this.data.width;
    canvas.height = this.data.height;
    this.container.appendChild(canvas);

    if (this.readOnly) {
      // Read-only mode: Display static chart and title
      const titleElement = document.createElement("h3");
      titleElement.textContent = this.data.title;
      titleElement.classList.add("chart-title");
      this.container.prepend(titleElement); // Add title above the chart

      setTimeout(() => this.renderChart(canvas), 100);
      return this.container;
    }

    // Editable mode: Render inputs and buttons
    const titleInput = document.createElement("input");
    titleInput.type = "text";
    titleInput.placeholder = "Enter chart title";
    titleInput.value = this.data.title;
    titleInput.addEventListener("input", (e) => {
      this.data.title = (e.target as HTMLInputElement).value;
    });

    this.container.prepend(titleInput); // Add input above the chart

    setTimeout(() => this.renderChart(canvas), 100);

    const block = document.createElement("div");
    block.appendChild(this.container);

    const editButton = document.createElement("button");
    editButton.textContent = "Edit Chart";
    editButton.className = "edit-chart-button my-3";
    editButton.onclick = () => this.openEditor();
    block.appendChild(editButton);

    return block;
  }

  private renderChart(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.error("Failed to get canvas context.");
      return;
    }

    // Destroy existing chart instance before rendering a new one
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }

    const chartType = this.data.type === "area" ? "line" : this.data.type; // Convert "area" to "line"

    // Ensure dataset is formatted correctly based on chart type
    const datasets = this.data.datasets.map((dataset) => {
      const modifiedDataset = { ...dataset };

      if (this.data.type === "scatter") {
        // Scatter charts require x,y pairs
        modifiedDataset.data = dataset.data.map((value, index) => {
          if (typeof value === "object") {
            return value;
          } else {
            return { x: index + 1, y: value }; // Default x values if not provided
          }
        });
      }

      if (this.data.type === "area") {
        // Convert Area to Line Chart with fill enabled
        modifiedDataset.fill = true;
        modifiedDataset.backgroundColor = dataset.backgroundColor.map(
          (color) => color + "80"
        ); // Semi-transparent
      }

      return modifiedDataset;
    });

    // Preserve existing labels and only assign missing ones
    if (this.data.type !== "scatter") {
      const datasetSize = this.data.datasets[0].data.length;

      // Ensure labels array is correctly sized
      if (this.data.labels.length < datasetSize) {
        this.data.labels = this.data.labels.map(
          (label, index) => label || `Point ${index + 1}`
        );

        // Add missing labels if necessary
        for (let i = this.data.labels.length; i < datasetSize; i++) {
          this.data.labels.push(`Point ${i + 1}`);
        }
      }
    }

    this.chartInstance = new Chart(ctx, {
      type: chartType as any, // "area" is mapped to "line"
      data: {
        labels: chartType === "scatter" ? undefined : this.data.labels, // Scatter charts don't need standard labels
        datasets: datasets,
      },
      options: {
        maintainAspectRatio: false,
        responsive: false,
        elements: {
          line: {
            tension: this.data.type === "area" ? 0.3 : 0, // Slight curve for area charts
          },
        },
        scales:
          chartType === "scatter"
            ? {
                x: { type: "linear", position: "bottom" }, // Ensure x-axis is numerical for scatter
                y: { beginAtZero: true },
              }
            : {},
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                if (this.data.type === "scatter") {
                  const index = tooltipItems[0].dataIndex;
                  return this.data.labels[index] || `Point ${index + 1}`;
                }
                return tooltipItems[0].label;
              },
              label: (tooltipItem: any) => {
                if (this.data.type === "scatter") {
                  const { x, y } = tooltipItem.raw as { x: number; y: number };
                  return `X: ${x}, Y: ${y}`;
                }
                return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
              },
            },
          },
        },
      },
    });
  }

  /**
   * Saves the current chart data, including title, type, labels, datasets, and dimensions.
   * @returns The saved chart data.
   */
  save(): ChartData {
    return {
      title: this.data.title || "New Chart",
      type: this.data.type || "bar",
      labels: this.data.labels.length
        ? this.data.labels
        : ["Label 1", "Label 2"],
      datasets: this.data.datasets.length
        ? this.data.datasets
        : [
            {
              label: "Default Dataset",
              data: [0],
              backgroundColor: backgroundColor,
            },
          ],
      width: parseInt(this.container!.style.width || "400"),
      height: parseInt(this.container!.style.height || "200"),
    };
  }

  /**
   * Opens a modal to edit chart configurations, such as type, labels, datasets, and dimensions.
   */
  openEditor(): void {
    const overlay = document.createElement("div");
    overlay.classList.add("chart-editor-overlay");

    const modal = document.createElement("div");
    modal.classList.add("chart-editor-modal");

    modal.innerHTML = `
      <div class="modal-header">
        <h3>Edit Chart</h3>
        <button class="close-button" id="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <div class="input-group">
          <label>Chart Type:</label>
          <select id="chartType" class="input-select">
            <option value="bar" ${
              this.data.type === "bar" ? "selected" : ""
            }>Bar</option>
            <option value="line" ${
              this.data.type === "line" ? "selected" : ""
            }>Line</option>
            <option value="pie" ${
              this.data.type === "pie" ? "selected" : ""
            }>Pie</option>
            <option value="scatter" ${
              this.data.type === "scatter" ? "selected" : ""
            }>Scatter</option>
            <option value="area" ${
              this.data.type === "area" ? "selected" : ""
            }>Area</option>
          </select>
        </div>
  
        <div class="input-group">
          <label>Chart Width (px):</label>
          <input type="number" id="chartWidth" class="input-field" value="${this.container?.style.width.replace(
            "px",
            ""
          )}" min="100">
          <p id="widthError" class="error-message" style="color: red; display: none;">Width must be at least 100px.</p>
        </div>
  
        <div class="input-group">
          <label>Chart Height (px):</label>
          <input type="number" id="chartHeight" class="input-field" min="100" value="${this.container?.style.height.replace(
            "px",
            ""
          )}" min="100">
          <p id="heightError" class="error-message" style="color: red; display: none;">Height must be at least 100px.</p>
        </div>
  
        <div id="datasetContainer" class="dataset-container"></div>
  
        <div class="input-group">
          <label>Labels (comma-separated):</label>
          <input type="text" id="chartLabels" class="input-field" value="${this.data.labels.join(
            ", "
          )}">
        </div>
      </div>
  
      <div class="modal-footer">
        <button id="saveChart" class="save-button disabled-button" disabled>Save</button>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    const widthInput = document.getElementById(
      "chartWidth"
    ) as HTMLInputElement;
    const heightInput = document.getElementById(
      "chartHeight"
    ) as HTMLInputElement;
    const widthError = document.getElementById("widthError")!;
    const heightError = document.getElementById("heightError")!;
    const saveButton = document.getElementById(
      "saveChart"
    ) as HTMLButtonElement;

    const chartTypeSelect = document.getElementById(
      "chartType"
    ) as HTMLSelectElement;
    const datasetContainer = document.getElementById(
      "datasetContainer"
    ) as HTMLDivElement;
    const labelsInput = document.getElementById(
      "chartLabels"
    ) as HTMLInputElement;

    // Validation function
    const validateInputs = () => {
      const isWidthValid = parseInt(widthInput.value, 10) >= 100;
      const isHeightValid = parseInt(heightInput.value, 10) >= 100;

      widthError.style.display = isWidthValid ? "none" : "block";
      heightError.style.display = isHeightValid ? "none" : "block";

      saveButton.disabled = !(isWidthValid && isHeightValid);

      // Toggle disabled class for grey effect
      if (saveButton.disabled) {
        saveButton.classList.add("disabled-button");
      } else {
        saveButton.classList.remove("disabled-button");
      }
    };

    // Validate on input change
    widthInput.addEventListener("input", validateInputs);
    heightInput.addEventListener("input", validateInputs);
    chartTypeSelect.addEventListener("input", validateInputs);
    datasetContainer.addEventListener("input", validateInputs);
    labelsInput.addEventListener("input", validateInputs);

    const renderDatasets = () => {
      datasetContainer.innerHTML = ""; // Clear previous datasets
      this.data.datasets.forEach((dataset, index) => {
        const datasetElement = document.createElement("div");
        datasetElement.classList.add("dataset-entry");
        datasetElement.innerHTML = `
          <div class="input-group">
            <label>Dataset ${index + 1} Name:</label>
            <input type="text" class="datasetName input-field" value="${
              dataset.label
            }">
          </div>
          <div class="input-group">
            <label class="datasetLabel">Values (${
              this.data.type === "scatter" ? "x:y pairs" : "comma-separated"
            }):</label>
            <input type="text" class="datasetValues input-field" 
                  placeholder="${
                    this.data.type === "scatter"
                      ? "e.g., 1:10, 2:20, 3:30"
                      : "e.g., 10, 20, 30"
                  }"
                  value="${
                    this.data.type === "scatter"
                      ? dataset.data
                          .map((d) =>
                            typeof d === "object" ? `${d.x}:${d.y}` : d
                          )
                          .join(", ")
                      : dataset.data.join(", ")
                  }">
          </div>
        `;
        datasetContainer.appendChild(datasetElement);
      });
    };

    // Initial render of dataset inputs
    renderDatasets();

    // Update dataset inputs when the chart type changes
    chartTypeSelect.addEventListener("change", () => {
      this.data.type = chartTypeSelect.value;
      renderDatasets(); // Re-render dataset inputs based on the selected type
    });

    document.getElementById("saveChart")!.addEventListener("click", () => {
      if (saveButton.disabled) return;

      this.data.type = chartTypeSelect.value;

      const width =
        (document.getElementById("chartWidth") as HTMLInputElement)
          .valueAsNumber || 400;
      const height =
        (document.getElementById("chartHeight") as HTMLInputElement)
          .valueAsNumber || 200;

      this.data.width = width; // Store in data
      this.data.height = height;

      this.container!.style.width = `${width}px`;
      this.container!.style.height = `${height}px`;

      // Update chart labels

      this.data.labels = labelsInput.value
        .split(",")
        .map((label) => label.trim());

      const datasetElements =
        datasetContainer.querySelectorAll(".dataset-entry");
      this.data.datasets = Array.from(datasetElements).map((el) => {
        const datasetName = (
          el.querySelector(".datasetName") as HTMLInputElement
        ).value;
        const datasetValues = (
          el.querySelector(".datasetValues") as HTMLInputElement
        ).value.trim();

        let formattedData;
        if (this.data.type === "scatter") {
          formattedData = datasetValues.split(",").map((pair) => {
            const [x, y] = pair.trim().split(":").map(Number);
            return { x, y };
          });
        } else {
          formattedData = datasetValues.split(",").map(Number);
        }

        return {
          label: datasetName,
          data: formattedData,
          backgroundColor: backgroundColor,
        };
      });

      this.updateChart();
      modal.remove();
      overlay.remove();
    });

    document.getElementById("closeModal")!.addEventListener("click", () => {
      modal.remove();
      overlay.remove();
    });

    overlay.addEventListener("click", () => {
      modal.remove();
      overlay.remove();
    });
  }

  /**
   * Updates the chart instance with the latest data and configurations.
   */
  updateChart(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy(); // Destroy old chart before updating
    }

    const canvas = this.container!.querySelector("canvas") as HTMLCanvasElement;
    if (canvas) {
      // Ensure the canvas dimensions match the new container size
      canvas.width = parseInt(this.container!.style.width, 10);
      canvas.height = parseInt(this.container!.style.height, 10);

      this.renderChart(canvas); // Re-render with updated data
    } else {
      console.error("Canvas element not found!");
    }
  }
}

export default ChartBlock;
